import alert_mixin from '@/utils/mixins/alert_mixin';
import { fetch_form_structure } from '@/services/main_section_service';
import { fetch_dependent_questions } from '@/services/question_service';

export default {
  name: 'PreviewForm',
  mixins: [alert_mixin],
  data: () => ({
    topBar: {
      title: 'Form Preview',
      exitLabel: 'Exit Preview',
    },
    formMeta: {
      eyebrow: 'Preview',
    },
    preview_loading: false,
    preview_error: '',
    preview_sections: [],
    active_main_section_index: 0,
  }),
  computed: {
    form_name() {
      return String(this.$route?.query?.form_name || '').trim() || 'Untitled Form';
    },
    form_id() {
      return Number(this.$route?.query?.form_id ?? null) || null;
    },
    total_main_sections() {
      return this.preview_sections.length;
    },
    active_main_section() {
      return this.preview_sections[this.active_main_section_index] || null;
    },
    section_progress_percent() {
      if (!this.total_main_sections) {
        return 0;
      }

      return Math.round(((this.active_main_section_index + 1) / this.total_main_sections) * 100);
    },
    section_title() {
      if (!this.active_main_section) {
        return 'No sections available';
      }

      return `Section ${this.active_main_section_index + 1}: ${this.active_main_section.main_section_name}`;
    },
    section_label() {
      if (!this.total_main_sections) {
        return 'Section 0 of 0';
      }

      return `Section ${this.active_main_section_index + 1} of ${this.total_main_sections}`;
    },
    next_button_label() {
      return this.active_main_section_index >= this.total_main_sections - 1
        ? 'End Of Preview'
        : 'Next Section';
    },
  },
  async created() {
    await this.load_form_preview();
  },
  methods: {
    normalize_dependent_question(question, index = 0) {
      const question_text =
        question?.question_text ??
        question?.question ??
        question?.text ??
        question?.name ??
        `Dependent Question ${index + 1}`;

      const question_type =
        question?.question_type?.question_type ??
        question?.question_type?.type ??
        question?.question_type ??
        question?.type ??
        question?.formatted_type ??
        '';

      return {
        id: question?.question_id ?? question?.id ?? `${Date.now()}-${index}`,
        text: String(question_text || '').trim() || `Dependent Question ${index + 1}`,
        type_label: String(question_type || '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (letter) => letter.toUpperCase())
          .trim(),
        trigger_condition: String(question?.trigger_condition ?? '').trim(),
        is_mandatory: typeof question?.is_mandatory === 'boolean' ? question.is_mandatory : null,
      };
    },
    normalize_question(question, index = 0) {
      const question_text =
        question?.question_text ??
        question?.question ??
        question?.text ??
        question?.name ??
        `Question ${index + 1}`;

      const question_type =
        question?.question_type?.question_type ??
        question?.question_type?.type ??
        question?.question_type ??
        question?.type ??
        '';

      const dependent_questions = Array.isArray(question?.dependent_questions)
        ? question.dependent_questions
        : Array.isArray(question?.dependant_questions)
          ? question.dependant_questions
          : [];

      const dependents_count_hint = dependent_questions.length;
      const embedded_dependents_have_text = dependent_questions.every((dep) =>
        Boolean(
          String(
            dep?.question_text ??
              dep?.question ??
              dep?.text ??
              dep?.name ??
              ''
          ).trim()
        )
      );

      return {
        id: question?.question_id ?? question?.id ?? `${Date.now()}-${index}`,
        text: String(question_text || '').trim() || `Question ${index + 1}`,
        type_label: String(question_type || '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (letter) => letter.toUpperCase())
          .trim(),
        dependent_questions: embedded_dependents_have_text
          ? dependent_questions.map((dep, dep_index) => this.normalize_dependent_question(dep, dep_index))
          : [],
        dependents_count_hint,
        dependents_loaded: embedded_dependents_have_text && dependents_count_hint > 0,
        dependents_loading: false,
        show_dependents: false,
      };
    },
    normalize_sub_section(sub_section, index = 0) {
      const questions = Array.isArray(sub_section?.questions)
        ? sub_section.questions.map((question, question_index) =>
            this.normalize_question(question, question_index)
          )
        : [];

      return {
        id: sub_section?.section_id ?? sub_section?.section ?? sub_section?.id ?? `${Date.now()}-${index}`,
        section_id: sub_section?.section_id ?? sub_section?.section ?? sub_section?.id ?? null,
        section_name: String(
          sub_section?.section_name ??
          sub_section?.name ??
          `Sub Section ${index + 1}`
        ).trim(),
        order_number: Number(sub_section?.order_number ?? index + 1),
        questions,
      };
    },
    normalize_main_section(main_section, index = 0) {
      const sub_sections = Array.isArray(main_section?.sub_sections)
        ? main_section.sub_sections.map((sub_section, sub_index) =>
            this.normalize_sub_section(sub_section, sub_index)
          )
        : [];

      return {
        id: main_section?.main_section_id ?? main_section?.main_section ?? main_section?.id ?? `${Date.now()}-${index}`,
        main_section_id:
          main_section?.main_section_id ?? main_section?.main_section ?? main_section?.id ?? null,
        main_section_name: String(
          main_section?.main_section_name ??
          main_section?.name ??
          `Main Section ${index + 1}`
        ).trim(),
        description: main_section?.description ?? '',
        order_number: Number(main_section?.order_number ?? index + 1),
        sub_sections: sub_sections.sort(
          (left, right) => Number(left.order_number) - Number(right.order_number)
        ),
      };
    },
    async load_form_preview() {
      if (!this.form_id) {
        this.preview_error = 'No form was selected for preview.';
        return;
      }

      this.preview_loading = true;
      this.preview_error = '';

      try {
        const items = await fetch_form_structure(this.form_id);
        this.preview_sections = items
          .map((item, index) => this.normalize_main_section(item, index))
          .sort((left, right) => Number(left.order_number) - Number(right.order_number));
        this.active_main_section_index = 0;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load the form preview.';
        this.preview_error = message;
        this.show_error(message);
      } finally {
        this.preview_loading = false;
      }
    },
    go_to_next_section() {
      if (this.active_main_section_index >= this.total_main_sections - 1) {
        return;
      }

      this.active_main_section_index += 1;
    },
    go_to_previous_section() {
      if (this.active_main_section_index <= 0) {
        return;
      }

      this.active_main_section_index -= 1;
    },
    async toggle_question_dependents(question) {
      if (!question) {
        return;
      }

      const next_state = !question.show_dependents;
      question.show_dependents = next_state;

      if (!next_state) {
        return;
      }

      if (question.dependents_loaded) {
        return;
      }

      const main_question_id = Number(question.id);

      if (!main_question_id) {
        return;
      }

      question.dependents_loading = true;

      try {
        const items = await fetch_dependent_questions(main_question_id);
        question.dependent_questions = Array.isArray(items)
          ? items.map((dep, dep_index) => this.normalize_dependent_question(dep, dep_index))
          : [];
        question.dependents_count_hint = question.dependent_questions.length;
        question.dependents_loaded = true;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load dependent questions.';
        this.show_error(message);
      } finally {
        question.dependents_loading = false;
      }
    },
  },
};
